import * as React from 'react';
import {
  Button,
  Content,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  Title
} from '@patternfly/react-core';
import { CurrentReleaseNotes, WhatsNewModal } from '@app/components/WhatsNew';
import { currentRelease, hasUnseenRelease, markReleaseSeen } from '@app/utils/releaseNotes';

/** Only ever one banner on screen, so a fixed id is safe. */
const TITLE_ID = 'login-banner-title';

/**
 * The panel that greets the user on the first sign-in after a new version is deployed.
 *
 * Deliberately built as a list of sections rather than around its one current occupant:
 * "What's new" is the first thing worth saying on arrival, not the only thing — planned
 * downtime, a policy change or a nudge about a new page all belong here too. Adding one is
 * a matter of adding a section below.
 *
 * Shown once per release: dismissing it records the version, so it stays quiet until the
 * next one ships. See `markReleaseSeen`.
 */
export const LoginBanner: React.FunctionComponent = () => {
  // Read once on mount. Re-reading on every render would hide the banner the moment the
  // cookie is written, losing the closing animation.
  const [isOpen, setIsOpen] = React.useState(hasUnseenRelease);
  const [isWhatsNewOpen, setIsWhatsNewOpen] = React.useState(false);

  const release = currentRelease();

  const dismiss = () => {
    markReleaseSeen();
    setIsOpen(false);
  };

  // Reading the full history is as good as reading the banner, so this counts as seeing the
  // release. The banner also closes rather than staying behind the notes: two open modals
  // both claim the screen reader by hiding every other child of <body>, and whichever
  // renders last wins, so the one in front is as likely to be the hidden one.
  const showAllNotes = () => {
    dismiss();
    setIsWhatsNewOpen(true);
  };

  if (!release) return null;

  return (
    <>
      {/* Left to itself PatternFly points aria-labelledby at the modal box, so the dialog is
          announced as its entire contents. Naming the title element gives it the heading. */}
      <Modal variant={ModalVariant.medium} isOpen={isOpen} onClose={dismiss} aria-labelledby={TITLE_ID}>
        <ModalHeader title="Welcome back to the Class Allocation System" labelId={TITLE_ID} />
        <ModalBody>
          <Content>
            {/* Section: what's new. Further sections go below, each with its own Title. */}
            <Title headingLevel="h2" size="lg">
              What&apos;s new in {release.version}
            </Title>
            <CurrentReleaseNotes showHeading={false} />
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button variant="primary" onClick={dismiss}>
            Got it
          </Button>
          <Button variant="link" onClick={showAllNotes}>
            See all release notes
          </Button>
        </ModalFooter>
      </Modal>

      <WhatsNewModal isOpen={isWhatsNewOpen} onClose={() => setIsWhatsNewOpen(false)} />
    </>
  );
};
